import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router'

import { PageHeader } from '../PageHeader'
import { Card, Pill, ProblemNotice, RecordingStatusPill, SegmentedControl } from '../ui'
import { getDailyStatus, listDayRecordings, type RecordingRecord } from '../../lib/api'
import { hasDailyStatus, readDailyStatus, type DailyStatusResult } from '../../lib/dailyStatus'
import { experimentDate, formatConfidence, formatLocalClock, localIsoDay, recordingLength } from '../../lib/format'
import { HISTORY_VIEW_OPTIONS, type HistoryView } from '../../lib/historyView'
import { CONDITION_WORDS, RECORDING_OUTCOME } from '../../lib/recordings'
import { useFanOut, type FanOutEntry } from '../../lib/useFanOut'
import { useResource } from '../../lib/useResource'
import { ContextStrip } from './ContextStrip'
import { DayAxis } from './DayAxis'
import { dailyReading, dayAxisKind, dayReadingKind, dayReadingWord, dayResearchBadge } from './dayReading'

const NO_ROWS: RecordingRecord[] = []

/** The Day view owns its filtered read; leaving it aborts both the list and fan-out. */
export function HistoryDay({ userId, onView }: { userId: string | null; onView: (view: HistoryView) => void }) {
  const [date, setDate] = useState(() => localIsoDay(new Date()))
  // Keep the date control mounted while its value changes so keyboard focus
  // survives. useResource fences the old date's response; account changes are
  // remounted by History's account key.
  return <DayForDate userId={userId} date={date} onDate={setDate} onView={onView} />
}

function DayForDate({ userId, date, onDate, onView }: {
  userId: string | null
  date: string
  onDate: (date: string) => void
  onView: (view: HistoryView) => void
}) {
  const load = useCallback((signal: AbortSignal) => userId === null
    ? Promise.resolve(NO_ROWS) : listDayRecordings(userId, date, { signal }), [userId, date])
  const list = useResource(load)
  const rows = list.data ?? NO_ROWS
  const keys = useMemo(() => rows.map((row) => row.recording_id), [rows])
  const statuses = useMemo(() => new Map(rows.map((row) => [row.recording_id, row.status])), [rows])
  const eligible = useCallback((id: string) => hasDailyStatus(statuses.get(id) ?? ''), [statuses])
  const loadResult = useCallback((id: string, signal: AbortSignal) => getDailyStatus(id, { signal }).then(readDailyStatus), [])
  const fan = useFanOut(keys, loadResult, { eligible })
  const daily = dailyReading(rows)
  const extras = rows.filter((row) => row.is_daily_reading !== true && row.status === 'scored')
  const resultFor = (row: RecordingRecord): DailyStatusResult | null => fan.entries[row.recording_id]?.value ?? null
  const badge = rows.map((row) => dayResearchBadge(resultFor(row))).find((value) => value !== null) ?? null
  const disclaimers = [...new Set(rows.flatMap((row) => {
    const result = resultFor(row)
    return result?.readable && result.disclaimer !== null ? [result.disclaimer] : []
  }))]
  const today = localIsoDay(new Date()) === date

  return (
    <div className="stack stack--wide experiment-day">
      <div className="experiment-day__heading">
        <PageHeader
          eyebrow={`History · ${experimentDate(date)}`}
          title={list.data === null ? 'Your day' : `${rows.length} ${rows.length === 1 ? 'reading' : 'readings'}${today ? ' today' : ''}`}
          lead={daily === null ? undefined : "One is the day's reading and sits in your baseline. Later readings are compared with it."}
        />
        <SegmentedControl label="History view" hideLabel options={HISTORY_VIEW_OPTIONS} value="day" onChange={onView} />
      </div>
      <label className="experiment-day__date">
        <span className="field__label">Day</span>
        <input type="date" value={date} onChange={(event) => { if (event.currentTarget.value !== '') onDate(event.currentTarget.value) }} />
      </label>
      {list.error !== null && <ProblemNotice error={list.error} onRetry={list.reload}>Your readings for this day could not be read.</ProblemNotice>}
      {list.isLoading && <p role="status">Loading this day’s readings…</p>}
      {list.data !== null && rows.length === 0 && <Card><p>No readings on this day.</p><Link className="btn btn--secondary" to="/record/connect">Take a reading</Link></Card>}
      {list.data !== null && rows.length !== 0 && <>
        <Card as="section" className="experiment-day__axis-card" aria-label="The day, by time">
          <div className="experiment-day__axis-head">
            <h2 className="experiment-day__label">The day, by time</h2>
            <div className="experiment-day__chips">
              {badge !== null && <Pill tone="purple">{badge}</Pill>}
              {daily !== null && <Pill tone="green" dot>In baseline · {formatLocalClock(daily.start_local_time)}</Pill>}
              {extras.length !== 0 && <Pill>Compared · {extras.map((row) => formatLocalClock(row.start_local_time)).join(' · ')}</Pill>}
            </div>
          </div>
          <DayAxis readings={rows.map((row) => ({
            id: row.recording_id,
            time: row.start_local_time,
            daily: row.is_daily_reading === true,
            word: dayReadingWord(resultFor(row)),
            kind: dayAxisKind(row),
          }))} />
        </Card>
        <Card as="section" className="experiment-day__rows" aria-label="Readings on this day">
          <ul>
            {rows.map((row) => <DayRow key={row.recording_id} row={row} daily={daily} entry={fan.entries[row.recording_id]} onRetry={() => fan.retry(row.recording_id)} />)}
          </ul>
        </Card>
      </>}
      <div className="experiment-day__explainers">
        <Card as="section" flat>
          <h2 className="experiment-day__label">Why only one enters the baseline</h2>
          <p>Your baseline is built from one reading per day at a similar time, so "usual" keeps its meaning. The first eyes-closed reading that scores is that day's reading; if it fails, the next one takes its place. Extra readings never move it and never count as extra days.</p>
        </Card>
        <Card as="section" flat>
          <h2 className="experiment-day__label">Compare any two</h2>
          <p>Any two readings from one day can be compared. The comparison says first whether they are comparable at all (same condition, same headband, quality, refit), then what differed, then whether the difference is bigger than your ordinary repeat variation.</p>
        </Card>
      </div>
      {disclaimers.map((disclaimer) => <p className="experiment-day__disclaimer" key={disclaimer}>{disclaimer}</p>)}
    </div>
  )
}

export function DayRow({ row, daily, entry, onRetry }: {
  row: RecordingRecord
  daily: RecordingRecord | null
  entry: FanOutEntry<DailyStatusResult> | undefined
  onRetry: () => void
}) {
  const result = entry?.status === 'ready' && entry.value?.readable ? entry.value : null
  const signal = result?.scores.find((score) => score.slot === 'signal_quality')
  const confidence = result == null ? null : formatConfidence(result.confidence)
  const word = dayReadingWord(result)
  const length = recordingLength(row.signal_rows)
  const compare = row.status === 'scored' && row.is_daily_reading !== true && daily !== null
  const reasons = result == null ? [] : [...new Set([...result.activeWarnings, ...(result.validity?.reasons ?? [])])]
  return (
    <li className="experiment-day__row" data-recording-id={row.recording_id}>
      <time className="experiment-day__time">{formatLocalClock(row.start_local_time)}</time>
      <div className="experiment-day__what">
        <h3>{dayReadingKind(row)} · {CONDITION_WORDS[row.condition]}{length !== null && ` · ${length}`}</h3>
        <div className="experiment-day__chips">
          {signal?.label != null && <Pill>{signal.display?.title ?? signal.slot} · {signal.label}</Pill>}
          {confidence !== null && <Pill tone={result?.lowConfidence ? 'amber' : 'neutral'}>{result?.lowConfidence && 'Held low · '}Confidence {confidence}</Pill>}
          {reasons.map((reason) => <Pill key={reason} tone="amber">{result?.validity?.reason_text?.[reason] ?? reason}</Pill>)}
          {row.status !== 'scored' && <RecordingStatusPill status={row.status} />}
          <ContextStrip report={row.self_report} />
        </div>
        {entry?.status === 'pending' || entry?.status === 'loading' || (hasDailyStatus(row.status) && entry === undefined) ? <span role="status">Loading this reading…</span> : null}
        {entry?.status === 'error' && <ProblemNotice error={entry.error} onRetry={onRetry}>This reading’s result could not be read.</ProblemNotice>}
        {entry?.status === 'ready' && entry.value?.readable === false && <p>{entry.value.detail}</p>}
        {row.status !== 'scored' && RECORDING_OUTCOME[row.status]?.detail != null && <p>{RECORDING_OUTCOME[row.status]?.detail}</p>}
        {row.error_code != null && <code>{row.error_code}</code>}
      </div>
      {word !== null && <Pill className="experiment-day__word">{word}</Pill>}
      <Link
        className="btn btn--secondary btn--sm experiment-day__action"
        to={compare ? `/history/compare/${encodeURIComponent(daily!.recording_id)}/${encodeURIComponent(row.recording_id)}` : `/history/${encodeURIComponent(row.recording_id)}`}
      >
        {compare ? `Compare with ${formatLocalClock(daily!.start_local_time)}` : 'Open'}
        <span className="visually-hidden"> reading at {formatLocalClock(row.start_local_time)}</span>
      </Link>
    </li>
  )
}
